import { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const nameValid = formData.name.trim() !== '';
    const emailValid = formData.email.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
    const messageValid = formData.message.trim() !== '';
    return nameValid && emailValid && messageValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/.netlify/functions/contact-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          message: formData.message.trim(),
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to send message');
      }

      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-rizes-ivory py-14 sm:py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="mb-9 max-w-2xl sm:mb-11">
          <p className="mb-2 text-xs font-semibold uppercase text-rizes-olive">Contact</p>
          <h2 className="section-title">Get in Touch</h2>
          <p className="section-subtitle mx-0">
            We&apos;d love to hear from you. Contact us for any inquiries or to book your stay.
          </p>
        </header>

        <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[0.9fr_1.1fr] xl:gap-8">
          {/* Contact Information */}
          <div className="space-y-5">
            <div className="rounded-lg border border-rizes-limestone bg-white p-6 sm:p-8">
              <h3 className="mb-6 font-serif text-2xl text-rizes-ink">Contact Information</h3>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <MapPin className="w-6 h-6 text-rizes-olive" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">Address</h4>
                    <p className="text-gray-600">Apollonia, Sifnos, Cyclades, Greece</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Phone className="w-6 h-6 text-rizes-olive" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">Phone</h4>
                    <p className="text-gray-600">+306983706589</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Mail className="w-6 h-6 text-rizes-olive" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">Email</h4>
                    <p className="text-gray-600">rizes.sifnos@gmail.com</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="overflow-hidden rounded-lg border border-rizes-limestone bg-white">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3187.6988682845194!2d24.716429611581805!3d36.96924815803588!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1498f3d05e8022ef%3A0x442c0861e6482c9f!2sRIZES%20Sifnos!5e0!3m2!1sen!2sgr!4v1790118953604!5m2!1sen!2sgr"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Sifnos Town Map"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <div className="rounded-lg bg-rizes-ivory p-6 sm:p-8 ring-1 ring-rizes-limestone/50">
            <h3 className="mb-6 font-serif text-2xl text-rizes-ink">Send Us a Message</h3>

            {submitStatus === 'success' && (
              <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4">
                <p className="text-sm font-medium text-green-800">Your message has been sent successfully! We&apos;ll get back to you soon.</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
                <p className="text-sm font-medium text-red-800">There was an error sending your message. Please try again later.</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Field */}
              <div className="space-y-2">
                <label htmlFor="name" className="block text-sm font-medium text-rizes-ink">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="block w-full rounded-md border-0 py-3 px-4 bg-white text-gray-900 ring-1 ring-inset ring-rizes-limestone placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-rizes-olive focus:outline-none sm:text-sm sm:leading-6 transition-colors motion-reduce:transition-none"
                  placeholder="Your name"
                />
              </div>

              {/* Email Field */}
              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-medium text-rizes-ink">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="block w-full rounded-md border-0 py-3 px-4 bg-white text-gray-900 ring-1 ring-inset ring-rizes-limestone placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-rizes-olive focus:outline-none sm:text-sm sm:leading-6 transition-colors motion-reduce:transition-none"
                  placeholder="your@email.com"
                />
              </div>

              {/* Message Field */}
              <div className="space-y-2">
                <label htmlFor="message" className="block text-sm font-medium text-rizes-ink">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="block w-full rounded-md border-0 py-3 px-4 bg-white text-gray-900 ring-1 ring-inset ring-rizes-limestone placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-rizes-olive focus:outline-none sm:text-sm sm:leading-6 transition-colors motion-reduce:transition-none resize-none"
                  placeholder="How can we help you?"
                />
                <p className="mt-1 text-xs text-gray-500">Feel free to ask about availability, the property, or Sifnos in general.</p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center items-center gap-2 rounded-lg bg-rizes-olive px-6 py-3 text-sm font-medium text-white hover:bg-rizes-olive-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rizes-olive focus-visible:ring-offset-2 transition-colors disabled:opacity-70 disabled:cursor-not-allowed motion-reduce:transition-none"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin motion-reduce:animate-none h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
